import { RecipeResponse, RecipesResponse } from '@/src/types/apps/gygRecipeTypes'
import { RecipeCountry } from '@/src/types/apps/recipeGuideTypes'
import { Recipe } from '@/src/views/components/recipe-mgmt/types'
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

interface RecipeQueryOptions {
  take?: number
  skip?: number
  sortBy?: string
  filters: Record<string, any>
}

interface RecipeInitialState {
  recipes: Recipe[]
  recipe: RecipeResponse['data'] | null
  stats: {
    total: number
    published: number
    draft: number
  }
  loading: boolean
  meta: RecipesResponse['meta']
  error: string | null
  success: string | null
}
const initialState: RecipeInitialState = {
  recipes: [],
  recipe: null,

  // Initialize stats with default values
  stats: {
    total: 0,
    published: 0,
    draft: 0
  },
  loading: false,
  meta: {
    total: 0,
    pages: 0
  },
  error: null,
  success: null
}

export const getRecipes = createAsyncThunk<
  RecipesResponse,
  RecipeQueryOptions,
  { rejectValue: any; extra: { httpClient: any } }
>('gygRecipes/getRecipes', async ({ take = 10, skip = 0, sortBy = '-id', filters }, { rejectWithValue, extra }) => {
  try {
    const response = await extra.httpClient.get(`recipes`, {
      params: { take, sortBy, filters, skip }
    })

    return response.data
  } catch (error: any) {
    return rejectWithValue(error.response.data)
  }
})

export const createRecipe = createAsyncThunk<
  RecipeResponse,
  Partial<RecipesResponse['data'][0]>,
  { rejectValue: any; extra: { httpClient: any } }
>('gygRecipes/createRecipe', async (recipe, { rejectWithValue, extra }) => {
  try {
    const response = await extra.httpClient.post('recipes', recipe)

    return response.data
  } catch (error: any) {
    return rejectWithValue(error.response.data)
  }
})

export const updateRecipe = createAsyncThunk<
  RecipeResponse,
  { id: number; data: Partial<RecipesResponse['data'][0]> },
  { rejectValue: any; extra: { httpClient: any } }
>('gygRecipes/updateRecipe', async ({ id, data }, { rejectWithValue, extra }) => {
  try {
    const response = await extra.httpClient.put(`recipes/${id}`, data)

    return response.data
  } catch (error: any) {
    return rejectWithValue(error.response.data)
  }
})

export const uploadRecipeFile = createAsyncThunk<
  RecipeResponse,
  { id: number; file: File; procedureId?: number; procedureUniqueId?: string; mediaField: string },
  { rejectValue: any; extra: { httpClient: any } }
>(
  'gygRecipes/uploadRecipeFile',
  async ({ id, file, procedureId, procedureUniqueId, mediaField }, { rejectWithValue, extra }) => {
    try {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('mediaField', mediaField)
      if (procedureId) {
        formData.append('procedureId', procedureId.toString())
      }
      if (procedureUniqueId) {
        formData.append('procedureUniqueId', procedureUniqueId)
      }

      const response = await extra.httpClient.post(`recipes/${id}/media`, formData)

      return response.data
    } catch (error: any) {
      return rejectWithValue(error.response.data)
    }
  }
)

export const getRecipe = createAsyncThunk<RecipeResponse, string, { rejectValue: any; extra: { httpClient: any } }>(
  'gygRecipes/getRecipe',
  async (id, { rejectWithValue, extra }) => {
    try {
      const response = await extra.httpClient.get(`recipes/${id}`)

      return response.data
    } catch (error: any) {
      return rejectWithValue(error.response.data)
    }
  }
)

export const deleteRecipe = createAsyncThunk<{ id: string }, string, { rejectValue: any; extra: { httpClient: any } }>(
  'gygRecipes/deleteRecipe',
  async (id, { rejectWithValue, extra }) => {
    try {
      await extra.httpClient.delete(`recipes/${id}`)

      return { id }
    } catch (error: any) {
      return rejectWithValue(error.response.data)
    }
  }
)

export const getRecipeStats = createAsyncThunk<
  any,
  { country: RecipeCountry },
  { rejectValue: any; extra: { httpClient: any } }
>('gygRecipes/getStats', async ({ country }, { rejectWithValue, extra }) => {
  try {
    const response = await extra.httpClient.get('recipes/stats', {
      params: { country }
    })

    return response.data
  } catch (error: any) {
    return rejectWithValue(error.response.data)
  }
})

const gygRecipeSlice = createSlice({
  name: 'gygRecipe',
  initialState,
  reducers: {
    clearGygRecipeSuccess(state) {
      state.success = null
    },
    clearGygRecipeError(state) {
      state.error = null
    }
  },
  extraReducers: builder => {
    builder.addCase(getRecipes.pending, state => {
      state.loading = true
      state.error = null
    })
    builder.addCase(getRecipes.fulfilled, (state, action) => {
      state.loading = false
      state.error = null
      state.success = null
      state.recipes = action.payload.data
      state.meta = action.payload.meta
    })
    builder.addCase(getRecipes.rejected, (state, action) => {
      state.loading = false
      state.error = action.payload || 'Failed to fetch recipes'
    })
    builder.addCase(createRecipe.pending, state => {
      state.loading = true
      state.error = null
      state.success = null
    })
    builder.addCase(createRecipe.fulfilled, (state, action) => {
      state.loading = false
      state.recipes.unshift(action.payload.data)
      state.recipe = action.payload.data
      state.meta.total += 1
      state.success = 'Recipe created successfully'
    })
    builder.addCase(createRecipe.rejected, (state, action) => {
      state.loading = false
      state.error = action.payload || 'Failed to create recipe'
    })

    builder.addCase(updateRecipe.pending, state => {
      state.loading = true
      state.error = null
      state.success = null
    })
    builder.addCase(updateRecipe.fulfilled, (state, action) => {
      state.loading = false
      state.recipe = action.payload.data
      state.success = 'Recipe updated successfully'
    })
    builder.addCase(updateRecipe.rejected, (state, action) => {
      state.loading = false
      state.error = action.payload || 'Failed to update recipe'
    })

    builder.addCase(getRecipe.pending, state => {
      state.loading = true
      state.error = null
    })
    builder.addCase(getRecipe.fulfilled, (state, action) => {
      state.loading = false

      // Optionally update or add the fetched recipe to the list
      const recipe = action.payload.data
      state.recipe = recipe
    })
    builder.addCase(getRecipe.rejected, (state, action) => {
      state.loading = false
      state.error = action.payload || 'Failed to fetch recipe'
    })

    builder.addCase(deleteRecipe.pending, state => {
      state.loading = true
      state.error = null
      state.success = null
    })
    builder.addCase(deleteRecipe.fulfilled, (state, action) => {
      state.loading = false
      state.recipes = state.recipes.filter(r => r.id !== +action.payload.id)
      state.meta.total -= 1
      state.success = 'Recipe deleted successfully'
    })
    builder.addCase(deleteRecipe.rejected, (state, action) => {
      state.loading = false
      state.error = action.payload || 'Failed to delete recipe'
    })

    builder.addCase(getRecipeStats.pending, state => {
      state.loading = true
      state.error = null
    })
    builder.addCase(getRecipeStats.fulfilled, (state, action) => {
      state.loading = false

      state.stats = {
        total: action.payload.data.totalRecipes,
        published: action.payload.data.publishedRecipes,
        draft: action.payload.data.draftRecipes
      }
    })
    builder.addCase(getRecipeStats.rejected, (state, action) => {
      state.loading = false
      state.error = action.payload || 'Failed to fetch stats'
    })
  }
})

export const { clearGygRecipeSuccess, clearGygRecipeError } = gygRecipeSlice.actions
export default gygRecipeSlice.reducer
